import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell } from 'react-table';
import { useRouteMatch, useHistory, useLocation } from 'react-router';
import { IntlShape, useIntl } from 'react-intl';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { TableColumn, TableRow, TableCell } from '@/components/atoms/Table/Table.type';
import { useLazyGetBrandUsersListQuery } from '@/services/innovorder/user/user.endpoints';
import { computeText } from '@/locales/utils';
import { formatDate } from '@/utils/date';
import { useLazyLoginAsGuestQuery, useMeQuery } from '@/services/innovorder/auth/auth.endpoints';
import { cookieLocationGuest, useAuthCookieHandler } from '@/hooks/useAuthCookieHandler/useAuthCookieHandler';
import { redirectToBackofficeV1 } from '@/utils/backoffice-v1';
import { GetBrandsUsersListResponse, UserRoles } from '@/services/innovorder/user/user.type';
import { SvyTooltip } from '@/components/savory/SvyTooltip';
import { getUser, getUserBrandId, getUserRole } from '@/redux/user';
import { AccessFlags } from '@/services/innovorder/auth/auth.type';
import { Routes } from '@/core/router/routes.types';
import { theme } from '@/core/theme';
import { SvyIconWithMargin } from './BrandsUsersList.style';
import { computeCookieDomain, COOKIE_PATH } from '../Login/Login';
import { fetchDataProps } from '../../components/atoms/Table/Table';

interface BrandsUsersListViewModel {
    rows: TableRow[];
    columns: TableColumn[];
    isLoading: boolean;
    data: GetBrandsUsersListResponse | undefined;
    url: string;
    isNewUserModalOpen: boolean;
    intl: IntlShape;
    canCreateUser: boolean;
    openNewUserModal: () => void;
    closeNewUserModal: () => void;
    handleRowClick: (
        cell: Cell<TableRow, TableCell<unknown>> | undefined,
        event?: React.MouseEvent<HTMLTableCellElement>,
    ) => void;
    fetchData: (fetchProps: fetchDataProps) => void;
}

const useBrandsUsersListVM = (): BrandsUsersListViewModel => {
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const { params, url } = useRouteMatch<{ brandId: string }>();
    const [, setCookie] = useCookies();

    const user = useSelector(getUser);
    const userRole = useSelector(getUserRole);
    const currentUserBrandId = useSelector(getUserBrandId);

    const canCreateUser = useMemo(() => {
        if (userRole === UserRoles.admin) {
            return true;
        }
        if (userRole === UserRoles.brand && user?.accessFlags?.[AccessFlags.CAN_MANAGE_BO_USERS]) {
            return true;
        }
        return false;
    }, [userRole, user?.accessFlags]);

    const [getBrandsList, { data, isLoading }] = useLazyGetBrandUsersListQuery();
    const [loginAsGuest, { isSuccess: loggedInAsGuest }] = useLazyLoginAsGuestQuery();
    const { token, isGuest } = useAuthCookieHandler();

    const [isNewUserModalOpen, setIsNewUserModalOpen] = useState<boolean>(false);
    const openNewUserModal = () => {
        setIsNewUserModalOpen(true);
    };

    const closeNewUserModal = () => {
        setIsNewUserModalOpen(false);
        // Remove the create query parameter from URL when closing modal
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('create')) {
            searchParams.delete('create');
            const newSearch = searchParams.toString();
            history.replace({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : '',
            });
        }
    };

    const { isFetching: isMeQueryFetching } = useMeQuery({ token }, { refetchOnMountOrArgChange: true });

    const rows: TableRow[] = useMemo(() => {
        if (!data) return [];
        return data.map((user) => ({
            lastName: {
                key: user.userId,
                type: 'string',
                value: {
                    text: user.lastName,
                },
            },
            firstName: {
                type: 'string',
                value: {
                    text: user.firstName,
                },
            },
            email: {
                type: 'string',
                value: {
                    text: user.email,
                },
            },
            lastConnection: {
                type: 'string',
                value: {
                    text: formatDate(user.lastConnection),
                },
            },

            ...(userRole === UserRoles.admin
                ? {
                      login: {
                          type: 'string',
                          value: {
                              text: (
                                  <>
                                      {user.role !== UserRoles.brandGroup ? (
                                          <SvyTooltip
                                              key={user.userId}
                                              tooltipContent={`${computeText(intl, 'brandUsers.loginAs')} ${
                                                  user.email
                                              }`}
                                          >
                                              <SvyIconWithMargin
                                                  onClick={(ev) => {
                                                      ev.stopPropagation();
                                                      loginAsGuestHandler(user.userId);
                                                  }}
                                                  icon="account-circle-fill"
                                              />
                                          </SvyTooltip>
                                      ) : (
                                          <SvyTooltip
                                              key={user.userId}
                                              tooltipContent={computeText(intl, 'brandUsers.loginAsBrandGroup')}
                                          >
                                              <SvyIconWithMargin
                                                  icon="group-2-fill"
                                                  style={{ color: theme.color.red100 }}
                                                  onClick={(ev) => {
                                                      ev.stopPropagation();
                                                  }}
                                              />
                                          </SvyTooltip>
                                      )}
                                  </>
                              ),
                          },
                      },
                  }
                : {}),
        }));
    }, [data, intl, userRole]);
    const columns: TableColumn[] = useMemo(() => {
        const columnArray = [
            {
                Header: computeText(intl, 'name.lastName'),
                accessor: 'lastName',
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: computeText(intl, 'name.firstName'),
                accessor: 'firstName',
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: computeText(intl, 'brandUsers.email'),
                accessor: 'email',
                disableFilters: true,
                disableSortBy: true,
            },
            {
                Header: computeText(intl, 'brandUsers.lastConnection'),
                accessor: 'lastConnection',
                disableFilters: true,
                disableSortBy: true,
            },
        ];
        if (userRole === UserRoles.admin) {
            columnArray.push({
                Header: computeText(intl, 'brandUsers.login'),
                accessor: 'login',
                disableFilters: true,
                disableSortBy: true,
            });
        }
        return columnArray;
    }, [intl, userRole]);

    const loginAsGuestHandler = useCallback(
        async (userId: number) => {
            const { accessToken } = await loginAsGuest({ userId }).unwrap();
            setCookie(
                cookieLocationGuest,
                { accessToken },
                { domain: computeCookieDomain(window.location.host), path: COOKIE_PATH },
            );
        },
        [setCookie],
    );

    const handleRowClick = useCallback(
        (cell: Cell<TableRow, TableCell<unknown>> | undefined, event?: React.MouseEvent<HTMLTableCellElement>) => {
            if (!cell) {
                return;
            }
            const url =
                userRole === UserRoles.admin
                    ? `/home/brands/${params.brandId}/users/${cell?.row.values.lastName.key}/info`
                    : `/home/brand/users/${cell?.row.values.lastName.key}/info`;

            redirectToBackofficeV1(url, event?.ctrlKey);
        },
        [userRole],
    );
    const fetchData = useCallback(({ globalFilter }: fetchDataProps) => {
        getBrandsList({ brandId: Number(params.brandId), search: globalFilter, orderByLastConnection: true });
    }, []);

    useEffect(() => {
        if (loggedInAsGuest && isGuest && !isMeQueryFetching) {
            redirectToBackofficeV1('/home');
        }
    }, [isMeQueryFetching, loggedInAsGuest, isGuest]);

    useEffect(() => {
        if (userRole !== UserRoles.admin && currentUserBrandId !== Number(params.brandId)) {
            history.replace(Routes.PermissionDenied);
        }
    }, [currentUserBrandId]);

    // Open modal if create=true query parameter is present
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('create') === 'true' && !isNewUserModalOpen) {
            setIsNewUserModalOpen(true);
        }
    }, [location.search, isNewUserModalOpen]);
    return {
        rows,
        columns,
        data,
        url,
        handleRowClick,
        isNewUserModalOpen,
        canCreateUser,
        openNewUserModal,
        closeNewUserModal,
        fetchData,
        isLoading,
        intl,
    };
};

export default useBrandsUsersListVM;
